import React, {useEffect, useState, Fragment} from 'react';
import {Link} from 'react-router-dom';
import axios from 'axios';
import {RESOURCES as resources} from '../productResources';
import {getGetHeaders} from '../utils/headers';
import ProductDetailDialog from '../components/ProductDetailDialog';
import ProductThumbnail from './ProductThumbnail';
import {makeStyles} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import InfoIcon from '@material-ui/icons/Info';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import SaveIcon from '@material-ui/icons/Save';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import OrderReceiptItemDeleteDialog from './OrderReceiptItemDeleteDialog';

const useStyles = makeStyles({
    btn: {
        marginTop: -4,
        marginRight: 4,
        padding: 2
    },
    btnInfo: {
        position: 'absolute',
        top: 20,
        right: 4,
        padding: 2
    },
    btnEditNotes: {
        display: 'inline-block',
        position: 'absolute',
        top: 4,
        left: 0,
        padding: 2
    },
    btnText: {
        marginRight: 6,
        padding: '0px 10px'
    },
    checkbox: {
        '& .MuiSvgIcon-root': {
            fontSize: 28
        }
    },
    message: {
        fontSize: 16,
        color: 'rgba(0, 0, 0, 0.54)'
    },
    messageError: {
        fontSize: 16,
        color: '#b4212d'
    },
    rowLocation: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        whiteSpace: 'normal'
    },
    rowLocationLink: {
        color: '#2d5033',
        fontWeight: 600,
    },
    rowLocationLinkEmpty: {
        color: 'rgba(0, 0, 0, 0.54)',
        fontWeight: 600,
    },
    saving: {
        color: 'rgba(0, 0, 0, 0.54)'
    },
    table: {},
    tHead: {},
    tBody: {},
    tr: {},
    trNotes: {
        backgroundColor: 'rgba(0, 0, 0, 0.03)'
    },
    th: {
        lineHeight: '1.2',
        verticalAlign: 'bottom'
    },
    thNotes: {
        position: 'relative',
        fontWeight: 500,
        padding: '10px 8px 9px 32px'
    },
    td: {
        position: 'relative',
        padding: '5px 16px 5px 16px',
        whiteSpace: 'normal',
        wordBreak: 'normal'
    },
    tdName: {
        position: 'relative',
        whiteSpace: 'normal',
        wordBreak: 'normal',
        padding: '5px 36px 5px 16px'
    },
    tdSmall: {
        position: 'relative',
        whiteSpace: 'normal',
        wordBreak: 'normal',
        padding: '5px 8px 5px 8px'
    },
    tdNoWrap: {
        position: 'relative',
        padding: '5px 5px 5px 5px',
        whiteSpace: 'nowrap'
    },
    noWrap: {
        whiteSpace: 'nowrap'
    }
});

/**
 * A component to display a list of Products for an Order
 *
 * @param view
 * @param onDeleteFunc
 * @param onEditQtyFunc
 * @param onEditNotesFunc
 * @param onRemoveFunc
 * @param itemList
 * @param hasDelete
 * @param hasEditNotes
 * @param hasEditQty
 * @param hasInfo
 * @param hasRemove
 * @param saving
 * @param error
 * @return {JSX.Element}
 * @constructor
 */
const OrderItemsList = ({
                            view,
                            onDeleteFunc,
                            onEditQtyFunc,
                            onEditNotesFunc,
                            onRemoveFunc,
                            itemList = [],
                            hasDelete = false,
                            hasEditNotes = false,
                            hasEditQty = false,
                            hasInfo = true,
                            hasRemove = true,
                            saving = false,
                            error = null
                        }) => {

    const [currentItem, setCurrentItem] = useState(null);
    const [currentItemId, setCurrentItemId] = useState(null);
    const [currentNoteItem, setCurrentNoteItem] = useState(null);
    const [qty, setQty] = useState(-1);
    const [availableQty, setAvailableQty] = useState(0);
    const [newQty, setNewQty] = useState(-1);
    const [saveRequest, setSaveRequest] = useState(false);
    const [saveMessage, setSaveMessage] = useState({
        error: false,
        message: ''
    });
    // State: Order Item to Delete with Dialog
    const [itemDeleteState, setItemDeleteState] = useState({
        dialog: false,
        item: null
    });
    // State: Product Detail Item with Dialog
    const [itemDetailState, setItemDetailState] = useState({
        dialog: false,
        item: null
    });

    // Effect: Fetch Product by ID
    useEffect(
        () => {
            if (currentItem && currentItem.product_id) {
                const fetchItem = () => {
                    const cfg = resources.Product.GET_ONE({id: currentItem.product_id});
                    axios({
                        method: 'GET',
                        url: cfg.uri,
                        headers: getGetHeaders()
                    }).then((response) => {
                        if (response.data && response.data.qty >= 0) {
                            // Set the maximum Quantity to request for this Product
                            setAvailableQty(response.data.qty);
                        }
                    }).catch((error) => {
                        console.log('error response for Product: ', error);
                        if (error.response) {
                            // Server response outside 2xx
                        } else if (error.request) {
                            // No response
                        }
                    });
                };

                // If current Item ID is set or has changed, fetch that Product
                if (currentItem && currentItem.product_id) {
                    fetchItem();
                }
            }

        }, [currentItem, qty]
    );

    // Effect: Reset items after an item has been updated
    useEffect(
        () => {
            if (currentItem && saveRequest && !saving) {
                handleEditQtyClose();
                setSaveMessage({error: false, message: (error) ? error : 'Item saved.'})
            }
        }, [currentItem, saveRequest, saving, error]
    );

    // All Views: Handle event from Info button
    const handleInfoClick = (item) => {
        setItemDetailState({dialog: !!(item), item: item});
    };

    const handleCloseItemDialog = () => {
        setItemDetailState({dialog: false, item: null});
    };

    // Create View: Handle event from Remove Item button
    const handleRemoveClick = (id) => {
        if (typeof onRemoveFunc === 'function') {
            let idx = itemList.findIndex(r => r.product_id === id);
            if (idx !== -1) {
                itemList[idx].qty_requested = 0;
                onRemoveFunc({item: itemList[idx], add: false, updateAvailable: true});
            }
        }
    };

    // Edit View: Handle event from Edit Qty button
    const handleEditQtyClick = (id) => {
        let idx = itemList.findIndex(r => r.product_id === id);
        if (idx !== -1) {
            const qty = itemList[idx].qty;
            // Display the New Qty input
            setQty(qty);
            setAvailableQty(qty);
            setNewQty(qty);
            setCurrentItem(itemList[idx]);
            setCurrentItemId(id);
            setSaveMessage({error: false, message: ''});
        }
    };

    // Edit View: Autofocus Edit input
    const handleAutoSelect = (e) => {
        e.target.select();
    };

    // Edit View: Handle event from change in New Qty field
    const handleEditQtyChange = (e, id) => {
        const value = (e.target && e.target.value) ? e.target.value : '';
        let qtyValue = value;
        const max = (view === 'create') ? availableQty : (availableQty + qty);
        if (value) {
            qtyValue = parseInt(value, 10);
            if (qtyValue > max) {
                qtyValue = max;
            }
        }
        setNewQty(qtyValue);
    };

    // Edit View: Handle event from Qty Save button
    const handleSaveEditQty = (id) => {
        if (typeof onEditQtyFunc === 'function') {
            setSaveRequest(true);
            currentItem.qty_requested = newQty;
            onEditQtyFunc({item: currentItem, new_qty: newQty, add: true, updateAvailable: true});
        }
    };

    // Edit View: Handle event from Edit Qty Cancel button
    const handleEditQtyClose = () => {
        setQty(-1);
        setAvailableQty(0);
        setNewQty(-1);
        setCurrentItem(null);
        setCurrentItemId(null);
        setSaveRequest(false);
    };

    const handleEditNotesClick = (item) => {
        setCurrentNoteItem(item);
    };

    const handleUpdateNotes = (e, item) => {
        item.notes = e.target.value;
        setCurrentNoteItem(item);
    };

    const handleSaveNotes = () => {
        if (typeof onEditNotesFunc === 'function') {
            onEditNotesFunc({item: currentNoteItem});
        }
        setCurrentNoteItem(null);
    };

    // Edit View: Handle event from Delete Item button
    const handleDeleteDialogClick = (item) => {
        setItemDeleteState({dialog: !!(item), item: item});
    };

    const handleDelete = () => {
        const item = itemDeleteState.item;
        if (typeof onDeleteFunc === 'function') {
            setSaveRequest(true);
            onDeleteFunc(item.id);
        }
        handleDeleteDialogClose();
    };

    const handleDeleteDialogClose = () => {
        setItemDeleteState({dialog: false, item: null});
    };

    // Generate rows of Locations per item row
    const setRowLocations = (locations) => {
        if (!locations) {
            locations = [{id: 0, location: "(None)", qty: 0}];
        }

        return (
            locations.map((item, index) => {
                    const isEmpty = (item.qty < 1);
                    const locClass = (isEmpty) ? classes.rowLocationLinkEmpty : classes.rowLocationLink;
                    return (
                        <span key={`order-item-locations-${item.id}-${index}`} className={classes.rowLocation}>
                            {item.id === 0 &&
                                <span>(None)</span>
                            }
                            {item.id !== 0 &&
                                <>
                                    <Link to={`/WarehouseLocation/${item.location}/show`}
                                          title={`Location ${item.location}`} className={locClass}>
                                        {item.location}
                                    </Link>
                                </>
                            }
                    </span>
                    )
                }
            )
        )
    };

    // Generate Order Item list rows
    const setRows = () => {
        const list = itemList;
        if (!list || list.length === 0) {
            return (
                <span key="file-empty">No Items Added</span>
            )
        }
        let cols = 3;
        if (hasRemove) {
            cols += 1;
        }
        if (hasEditQty) {
            cols += 2;
        }
        if (hasDelete) {
            cols += 1;
        }

        return (
            itemList.map((item, index) => (
                <Fragment key={`order-item-group-${item.title}-${index}`}>
                    <TableRow className={classes.tr} key={`order-item-${item.title}-${index}`}>

                        {hasEditQty &&
                            <TableCell className={classes.tdSmall}>
                                <Checkbox
                                    color="primary"
                                    className={classes.checkbox}
                                />
                            </TableCell>
                        }
                        <TableCell className="td-sku">
                            {item.sku}
                            {hasInfo &&
                                <IconButton
                                    aria-label="Info"
                                    className={classes.btnInfo}
                                    onClick={() => handleInfoClick(item)}
                                >
                                    <InfoIcon/>
                                </IconButton>
                            }
                        </TableCell>

                        <TableCell className={classes.td}>
                            {/* product_modified is the product row's time (api-mgmt#36), the one an image
                                upload moves; item.modified is the order item's own and must not version the image. */}
                            <ProductThumbnail label="Image" id={item.id} title={item.title} thumbnail={item.thumbnail} modified={item.product_modified}/>
                        </TableCell>

                        <TableCell className="td-title">{item.show_name}</TableCell>

                        <TableCell className={classes.td}>{item.type_name}</TableCell>

                        <TableCell className="td-name">{item.title}</TableCell>

                        <TableCell className={classes.td}>
                            {[...setRowLocations(item.warehouse_locations)]}
                        </TableCell>

                        <TableCell align="right" className={classes.tdNoWrap}>{item.qty}</TableCell>

                        {hasEditQty &&
                            <TableCell className={classes.td}>
                                {(!currentItemId || currentItemId !== item.product_id) &&
                                    <IconButton
                                        aria-label="Edit Qty"
                                        className={classes.btn}
                                        onClick={() => handleEditQtyClick(item.product_id)}
                                        disabled={!!(currentItemId && currentItemId !== item.product_id)}
                                    >
                                        <EditIcon/>
                                    </IconButton>
                                }
                                {currentItemId && currentItemId === item.product_id &&
                                    <>
                                        {!saving &&
                                            <>
                                                <TextField
                                                    type="number"
                                                    variant="outlined"
                                                    id={`edit-order-item-qty-${index}`}
                                                    label=""
                                                    value={newQty}
                                                    autoFocus={true}
                                                    className="input-table-small number"
                                                    onFocus={handleAutoSelect}
                                                    onChange={(e) => handleEditQtyChange(e, item.product_id)}
                                                    InputProps={{
                                                        inputProps: {
                                                            max: (view === 'create') ? (availableQty) : (availableQty + item.qty),
                                                            min: 0,
                                                            step: 1,
                                                            autoFocus: true,
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className={classes.btnText}
                                                    startIcon={<SaveIcon/>}
                                                    onClick={() => handleSaveEditQty(item.product_id)}
                                                    disabled={(qty === newQty)}
                                                >
                                                    Update
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="default"
                                                    className={classes.btnText}
                                                    startIcon={<CloseIcon/>}
                                                    onClick={() => handleEditQtyClose()}
                                                >
                                                    Cancel
                                                </Button>
                                            </>
                                        }
                                        {saving &&
                                            <span className={classes.saving}>Saving...</span>
                                        }
                                    </>
                                }
                            </TableCell>
                        }

                        {hasRemove &&
                            <TableCell className={classes.td}>
                                <IconButton
                                    aria-label="Remove"
                                    className={classes.btn}
                                    onClick={() => handleRemoveClick(item.product_id)}
                                >
                                    <RemoveCircleOutlineIcon/>
                                </IconButton>
                            </TableCell>
                        }

                        {hasDelete &&
                            <TableCell className={classes.td}>
                                <IconButton
                                    aria-label="Delete"
                                    className={classes.btn}
                                    onClick={() => handleDeleteDialogClick(item)}
                                    disabled={!!(currentItemId)}
                                >
                                    <HighlightOffIcon/>
                                </IconButton>
                            </TableCell>
                        }
                    </TableRow>
                    {(item.notes !== null && (hasEditNotes || item.notes !== '')) &&
                        <TableRow className={classes.trNotes} key={`order-item-notes-${item.title}-${index}`}>
                            <TableCell colSpan={2} className={classes.td}></TableCell>
                            <TableCell align="right" className={classes.thNotes}>
                                {hasEditNotes &&
                                    <IconButton
                                        aria-label="Edit Notes"
                                        className={classes.btnEditNotes}
                                        onClick={() => handleEditNotesClick(item)}
                                    >
                                        <EditIcon/>
                                    </IconButton>
                                }
                                <span>Notes:</span>
                            </TableCell>
                            <TableCell colSpan={cols} className={classes.td}>
                                {(!currentNoteItem || currentNoteItem.product_id !== item.product_id) &&
                                    <span>
                                        {item.notes}
                                    </span>
                                }
                                {currentNoteItem && currentNoteItem.product_id === item.product_id &&
                                    <>
                                        <TextField
                                            variant="outlined"
                                            id={`edit-product-item-notes-${item.id}`}
                                            label=""
                                            defaultValue={item.notes}
                                            className="input-table-small update mr-1"
                                            onChange={(e) => handleUpdateNotes(e, item)}
                                        />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className={classes.btnText}
                                            startIcon={<SaveIcon/>}
                                            onClick={() => handleSaveNotes()}
                                        >
                                            Update
                                        </Button>
                                    </>
                                }
                            </TableCell>
                            <TableCell className={classes.td}></TableCell>
                        </TableRow>
                    }
                </Fragment>
            ))
        )
    };

    const classes = useStyles();

    if (itemList.length === 0) {
        return (
            <div className="order-item-list-container">
                <Table className={classes.table} size="small" stickyHeader aria-label="Added Items">
                    <TableHead className={classes.tHead}>
                        <TableRow>
                            <TableCell className={classes.th}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className={classes.tBody}>
                        <TableRow className={classes.tr}>
                            <TableCell className={classes.th}>No Products in this Order</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        )
    }

    return (
        <div className="order-item-list-container">
            {itemList.length > 0 &&
                <>
                    <Table className={classes.table} size="small" stickyHeader aria-label="Added Items">
                        <TableHead className={classes.tHead}>
                            <TableRow>
                                {hasEditQty &&
                                    <TableCell className={classes.th}></TableCell>
                                }
                                <TableCell className={classes.th}>SKU</TableCell>
                                <TableCell className={classes.th}>Image</TableCell>
                                <TableCell className={classes.th}>Title</TableCell>
                                <TableCell className={classes.th}>Category</TableCell>
                                <TableCell className={classes.th}>Product</TableCell>
                                <TableCell className={classes.th}>Locations</TableCell>
                                <TableCell align="right" className={classes.th}>
                                    <span className="hide-print">Requested<br/></span>Qty
                                </TableCell>
                                {hasEditQty &&
                                    <TableCell className={classes.th}>Edit<br/>Qty</TableCell>
                                }
                                {hasRemove &&
                                    <TableCell className={classes.th}>Remove</TableCell>
                                }
                                {hasDelete &&
                                    <TableCell className={classes.th}>Delete</TableCell>
                                }
                            </TableRow>
                        </TableHead>
                        <TableBody className={classes.tBody}>
                            {[...setRows()]}
                        </TableBody>
                    </Table>
                    {saveMessage.message !== '' &&
                        <span className={(saveMessage.error) ? classes.messageError : classes.message}>
                            {saveMessage.message}
                        </span>
                    }
                    <ProductDetailDialog
                        openDialog={itemDetailState.dialog}
                        productId={(itemDetailState.item) ? itemDetailState.item.product_id : null}
                        onCloseFunc={handleCloseItemDialog}
                    />
                    <OrderReceiptItemDeleteDialog
                        type="Order"
                        openDialog={itemDeleteState.dialog}
                        item={(itemDeleteState.item) ? itemDeleteState.item : null}
                        onCloseFunc={() => handleDeleteDialogClose()}
                        onDeleteFunc={() => handleDelete()}
                    />
                </>
            }
        </div>
    )
}

export default OrderItemsList;
